/**
 * AdminOnboardingRequests.jsx — client_admin view of driver ↔ tenant
 * first-touch bookkeeping.
 *
 * Surfaces rows from `lbs_driver_tenant_onboarding` (existed since
 * Feb 2026 but had no UI). Two operational needs solved:
 *   1. Answer "have we ever seen this driver before, and did we
 *      welcome them?"
 *   2. Give the client_admin a "resend welcome" action for the drivers
 *      whose welcome email bounced / went unsent (RLS-blocked queue,
 *      SMTP outage, etc.).
 *
 * Ships 2026-07-14 as P3a-1 of the post-R7 backlog.
 */
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Truck, RefreshCw, Send, Search, MailCheck, Clock } from "lucide-react";
import { listDriverOnboarding, resendDriverWelcome } from "@/lib/api/onboarding";
import { useViewAsClient } from "@/lib/viewAsClient";
import { useAuth } from "@/lib/auth";

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
}

function driverName(row) {
  const d = row.driver || {};
  const full = [d.first_name, d.last_name].filter(Boolean).join(" ");
  return full || d.email || "(unknown driver)";
}

export default function AdminOnboardingRequests() {
  const { viewAsClientId } = useViewAsClient() || {};
  const activeClientId = viewAsClientId;   // local alias for readability
  const { roles } = useAuth();
  const isSuperAdmin = roles?.includes("super_admin");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [resending, setResending] = useState(null);
  const [migrationMissing, setMigrationMissing] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await listDriverOnboarding({ clientId: activeClientId });
    setLoading(false);
    if (error) {
      // Migration 034 not applied on this environment → surface a
      // helpful, non-noisy message rather than a raw PostgREST toast.
      const msg = String(error.message || "");
      if (/schema cache|not find the table|PGRST205/i.test(msg)) {
        setMigrationMissing(true);
        setRows([]);
        return;
      }
      toast.error(msg);
      return;
    }
    setMigrationMissing(false);
    setRows(data || []);
  }, [activeClientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const d = r.driver || {};
    return (
      (d.first_name || "").toLowerCase().includes(q) ||
      (d.last_name  || "").toLowerCase().includes(q) ||
      (d.email      || "").toLowerCase().includes(q) ||
      (d.phone      || "").toLowerCase().includes(q)
    );
  });

  const doResend = async (row) => {
    setResending(row.id);
    const { error } = await resendDriverWelcome({
      onboardingId: row.id,
      clientId:     row.client_id,
      driverUserId: row.driver_user_id,
      driverEmail:  row.driver?.email,
    });
    setResending(null);
    if (error) { toast.error(error.message); return; }
    toast.success("Welcome email queued — will send on next drain (≤60s).");
    refresh();
  };

  const pendingCount   = rows.filter((r) => !r.welcome_sent_at).length;
  const welcomedCount  = rows.length - pendingCount;

  if (isSuperAdmin && !activeClientId) {
    return (
      <div
        className="p-6 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600"
        data-testid="admin-onboarding-page-gate"
      >
        Pick a tenant via the top-nav <strong>View as client</strong> picker
        to see their driver onboarding history.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-onboarding-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Truck className="w-6 h-6 text-slate-500" />
            Driver onboarding
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Every driver who has been assigned to at least one booking under
            this tenant. If the welcome email has not sent, use
            <strong> Resend welcome</strong> to queue it via the outbox.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
            <MailCheck className="w-3 h-3 mr-1" />
            {welcomedCount} welcomed
          </Badge>
          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
            <Clock className="w-3 h-3 mr-1" />
            {pendingCount} pending
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            aria-label="Search drivers by name, email or phone"
            placeholder="Search drivers…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="onboarding-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="onboarding-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Onboarding records
            <span className="ml-2 text-xs font-normal text-slate-500">
              ({filtered.length}{filtered.length !== rows.length ? ` of ${rows.length}` : ""})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {migrationMissing && (
            <div
              className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              data-testid="onboarding-migration-missing"
            >
              <strong>Migration 034 not applied yet.</strong> Ship
              <code className="mx-1">spec_review/100-features/034_driver_tenant_onboarding.sql</code>
              in the Supabase dashboard to enable this page.
            </div>
          )}
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Driver</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>First seen</TableHead>
                  <TableHead>Welcome status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No drivers on record for this tenant yet.</TableCell></TableRow>
                ) : filtered.map((row) => (
                  <TableRow key={row.id} data-testid={`onboarding-row-${row.driver_user_id}`}>
                    <TableCell className="font-medium">{driverName(row)}</TableCell>
                    <TableCell className="text-xs text-slate-600">
                      <div>{row.driver?.email || "—"}</div>
                      <div>{row.driver?.phone || ""}</div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{fmtDateTime(row.first_seen_at)}</TableCell>
                    <TableCell>
                      {row.welcome_sent_at ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          Sent {fmtDateTime(row.welcome_sent_at)}
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800">Pending / not sent</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!row.driver?.email || resending === row.id}
                        onClick={() => doResend(row)}
                        data-testid={`onboarding-resend-${row.driver_user_id}`}
                      >
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        {resending === row.id ? "Queuing…" : "Resend welcome"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
